import { PureComponent, ComponentType } from 'react';
import cx from 'classnames';
import _ from 'lodash';
import { withPropsOnChange, withState } from 'recompose';

import { parseQuery, getExpression } from '../../utils/utils';
import type { ExpressionComponent } from '../../utils/utils';
import { getRandomName } from '../../utils/utils';
import WithFormulaData from '../../decorators/WithFormulaData';

import { CheckerMessage } from '../CheckerExpression';
import Select from '../../components/Select';
import TextArea from '../../components/TextArea/TextArea';
import TextField from '../../components/TextField/TextField';
import TextInput from '../../components/TextInput';
import { InlineCheckerMessage, Label as CheckerLabel } from '../CheckerExpression';
import type { Dispatch } from 'redux';
import type { UiFormulaNode, UiLog } from '../../typeDefs';

import './ExpressionSelector.scss';
import {
    AddArtifacts,
    Content,
    Expression,
    Extended,
    ExtendedContent,
    Label,
    LabelCollapse,
    Log,
    Remove,
    RootContainer,
    Toggle,
} from './styles';
import ExpressionOptions from './ExpressionOptions';

const DEBUG = parseQuery().debug === 'true';

const blockOperations = _.keyBy(['and', 'or', 'if', 'for', 'call', 'checker-list', 'checker-table']);
export const opIsBlock = ($op: string, _value?: unknown): boolean => !!blockOperations[$op];

interface DisplayArgs {
    type?: string;
    parentType?: string | null;
    display?: string;
    root?: boolean;
    result?: unknown;
    parentResult?: unknown;
    lastChild?: unknown;
    extendedLayout?: boolean;
}

interface ExpressionSelectorProps {
    data: UiFormulaNode;
    isReadOnly: boolean;
    noRemove?: boolean;
    path?: string;
    depth?: number;
    dispatch?: Dispatch;
    isRoot?: boolean;
    dontIndent?: boolean;
    'data-last-child'?: unknown;
    [key: string]: unknown;
}

interface ExpressionSelectorState {
    showOptions: boolean;
    highlight: boolean;
    showArtifacts: boolean;
    forceShowArtifacts: boolean;
    selfCollapsed: boolean | null;
}

class ExpressionSelector extends PureComponent<ExpressionSelectorProps, ExpressionSelectorState> {
    state: ExpressionSelectorState = {
        showOptions: false,
        highlight: false,
        showArtifacts: false,
        forceShowArtifacts: false,
        selfCollapsed: null,
    };

    _update: Record<string, string> = {};

    UNSAFE_componentWillReceiveProps(nextProps: ExpressionSelectorProps) {
        if (nextProps.data !== this.props.data && (!nextProps.data.log || !nextProps.data.log.hasLog)) {
            this.setState({ showArtifacts: false, forceShowArtifacts: false });
        }
    }

    get isCollapsed(): boolean {
        const { selfCollapsed } = this.state;
        const { data: { defaultCollapsed } } = this.props;

        return selfCollapsed != null ? selfCollapsed : defaultCollapsed;
    }

    updateComment() {
        const { data } = this.props;
        if (!this._update || !Object.keys(this._update).length) return;
        data.change(this._update);
    }

    onChange = ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>) => {
        const { data } = this.props;

        if (value === 'clipboard') return data.clipboardPaste();

        if (!value.includes(':')) return data.replace(value);

        const [name, ...args] = value.split(':');
        data.replace(name, args);
    };

    onNotChange = ({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) => this.props.data.change('$not', checked);

    getInputHandler(key: string) {
        return (event: React.ChangeEvent<HTMLInputElement>) => this._update[key] = event.target.value.trim();
    }

    handleShowMenu = () => {
        if (this.props.isReadOnly) return;
        this._update = {};
        this.setState({ showOptions: true });
    };

    handleHideMenu = () => {
        this.setState({ showOptions: false });
        this.updateComment();
    };

    handleUpdateComment = () => this.updateComment();

    handleCollapseToggle = () => {
        const next = !this.isCollapsed;

        this.setState({
            selfCollapsed: next === this.props.data.defaultCollapsed ? null : next,
        });
    };

    renderLabel({ Klass, displayProps, isBlock }: { Klass: ExpressionComponent; displayProps: Record<string, unknown>; isBlock: boolean }) {
        const { data, noRemove, isReadOnly } = this.props;
        const { showOptions, showArtifacts, forceShowArtifacts } = this.state;

        return <Label
            key='label'
            {...displayProps}
            {...(!data.log.hasLog ? null : {
                onMouseEnter: (event: React.MouseEvent) => {
                    event.stopPropagation();
                    this.setState({ showArtifacts: true });
                },
                onMouseLeave: (event: React.MouseEvent) => {
                    event.stopPropagation();
                    this.setState({ showArtifacts: false });
                },
                onClick: () => this.setState({ forceShowArtifacts: !forceShowArtifacts }),
            })}
        >
            {isBlock &&
                <LabelCollapse onClick={this.handleCollapseToggle}>
                    {this.isCollapsed ? '[+]' : '[-]'}
                </LabelCollapse>
            }
            <Actions
                data={data}
                noRemove={noRemove}
                isReadOnly={isReadOnly}
                showOptions={showOptions}
                handleShowMenu={this.handleShowMenu}
                handleHideMenu={this.handleHideMenu}
                onChange={this.onChange}
                onNotChange={this.onNotChange}
                extra={!Klass.renderActions ? null : Klass.renderActions(data)}
            />

            {data.log.hasLog && (showArtifacts || forceShowArtifacts)
                ? <Artifacts key='log' log={data.log} />
                : null
            }
        </Label>;
    }

    render() {
        const { data, noRemove, isReadOnly, path, dispatch, isRoot: iDontCare, dontIndent, ...rest } = this.props;

        const Klass = getExpression(data.$op)!;

        const isRoot = dontIndent || !data.state.parentType;
        const extendedLayout = !!(data.$comment || data.$checker || data.$artifacts);
        const isBlock = opIsBlock(data.$op);
        const isCollapsed = isBlock && this.isCollapsed;

        const displayArgs: DisplayArgs = {
            type: data.$op,
            parentType: data.state.parentType,
            display: isBlock && !isCollapsed ? 'block' : 'inline',
            root: isRoot,
            result: !data.log.hasLog ? '' : data.log.result,
            parentResult: !data.log.hasLog ? '' : data.log.parentResult,
            lastChild: isRoot ? '' : this.props['data-last-child'],
            extendedLayout,
        };

        const displayProps = getDisplayProps(displayArgs);

        const renderLabel = () => this.renderLabel({ Klass, displayProps, isBlock });

        const renderContent = () =>
            isCollapsed
                ? <Content Tag='i' key='content' {...displayProps} data-display='inline'>(collapsed)</Content>
                : <Content key='content' {...displayProps}>
                    <Klass data={data} />
                </Content>;

        const child = (
            <Container
                key={data.ID}
                displayArgs={displayArgs}
                {...rest}
            >
                {!extendedLayout ? [renderLabel(), renderContent()] :
                    <div>
                        <Extended {...displayProps}>
                            {!data.$comment ? null :
                                <TextArea
                                    defaultValue={data.$comment}
                                    onChange={text => data.change('$comment', text || undefined)}
                                    className='ExpressionSelector-comment'
                                    readOnly={isReadOnly}
                                />
                            }

                            {!data.$checker ? null : <InlineChecker data={data} />}

                            {!data.$artifacts ? null :
                                <div className='row' style={{ margin: 0 }}>
                                    <AddArtifacts>
                                        <Remove onClick={() => data.change('$artifacts', undefined)} />
                                        <span>Store artifacts in:</span>
                                        <TextInput
                                            defaultValue={data.$artifacts}
                                            onChange={value => {
                                                value = value.replace(/[^a-z_]/gi, '').trim();
                                                value = value || getRandomName();
                                                data.change('$artifacts', value);
                                            }}
                                        />
                                    </AddArtifacts>
                                </div>
                            }
                        </Extended>

                        <ExtendedContent {...displayProps}>
                            {renderLabel()}
                            {renderContent()}
                        </ExtendedContent>
                    </div>
                }
            </Container>
        );

        if (!isRoot) return child;

        return <RootContainer data-colors={data.log.hasLog ? 'log' : 'colors'}>
            {child}
        </RootContainer>;
    }
}

export default WithFormulaData(
    withPropsOnChange(['data'], ({ data }: { data: UiFormulaNode }) => ({
        isReadOnly: !!data.state.readOnly,
    }))(ExpressionSelector as ComponentType<any>) as ComponentType<any>,
) as unknown as ExpressionComponent;

const getDisplayProps = ({ type, parentType, display, root, result, parentResult, lastChild, extendedLayout }: DisplayArgs): Record<string, unknown> => ({
    'data-type': type,
    'data-parent-type': !parentType ? 'null' : parentType,
    'data-display': display,
    'data-root': root ? '' : undefined,
    'data-result': result == null ? 'null' : String(Boolean(result)),
    'data-parent-result': parentResult == null ? 'null' : String(Boolean(parentResult)),
    'data-last-child': root ? '' : lastChild,
    'data-extended': extendedLayout ? '' : undefined,
});

interface ContainerProps {
    displayArgs?: DisplayArgs;
    label?: React.ReactNode;
    children?: React.ReactNode;
    [key: string]: unknown;
}

export const Container = ({
    displayArgs = {},
    label,
    children,
    ...props
}: ContainerProps) =>
    <Expression
        {...getDisplayProps(displayArgs)}
        {...props}
    >
        {!label ? null :
            <Label {...getDisplayProps(displayArgs)}>{label}</Label>
        }
        {children}
    </Expression>;

interface ActionsProps {
    data: UiFormulaNode;
    isReadOnly: boolean;
    handleShowMenu: () => void;
    handleHideMenu: () => void;
    onNotChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    noRemove?: boolean;
    showOptions?: boolean;
    extra?: React.ReactNode;
}

const Actions = ({
    data,
    isReadOnly,
    handleShowMenu,
    handleHideMenu,
    onNotChange,
    onChange,
    noRemove,
    showOptions,
    extra,
}: ActionsProps) => {
    const opValue = data.$op === 'fn' ? `fn:${data.$value}` :
        data.$op === 'call' ? `call:${data.$value}` :
        data.$op;

    return (
        <div className={'ExpressionSelector-actions'}>
            <div
                onMouseLeave={handleHideMenu}
                className={cx('ExpressionSelector-actionsInner')}
            >
                {!data.$not ? null : <div className='ExpressionSelector-not'>not</div>}

                <Select
                    value={opValue}
                    onChange={onChange}
                    options={data.operationOptions.filter(x => x[2] !== 'Deprecated' || x[0] === 'schema')}
                    onMouseEnter={handleShowMenu}
                    readOnly={isReadOnly}
                />

                {extra}

                {showOptions &&
                    <ExpressionOptions data={data} onNotChange={onNotChange} noRemove={noRemove} />
                }
            </div>
        </div>
    );
};

const ArtifactsInner = ({ log, extended, setExtended }: { log: UiLog; extended: boolean; setExtended: (v: boolean) => void }) => {
        const print = (obj: unknown) =>
            JSON.stringify(obj, null, 4)
                .split('\n')
                .slice(1, -1)
                .map(line => line.slice(4))
                .join('\n');

        const getVal = (obj: unknown): unknown =>
            extended || !obj || typeof obj !== 'object' ? obj : ('$value' in obj ? (obj as { $value: unknown }).$value : obj);

        const artifacts = log.artifacts;

        return (
            <Log onClick={(event: React.MouseEvent) => event.stopPropagation()}>
                {!DEBUG ? null :
                    <div className='debug'>{log.path}</div>
                }

                <Toggle onClick={() => setExtended(!extended)}>
                    {extended ? '-' : '+'}
                </Toggle>

                <b>{extended ? 'Extended value: ' : ''}</b>

                {
                    artifacts == null ? '<<No Artifacts>>' :
                    typeof artifacts !== 'object' ? (artifacts as React.ReactNode) :
                    Array.isArray(artifacts) ? print(artifacts.map(getVal)) :
                    print(getVal(artifacts))
                }
            </Log>
        );
};

const Artifacts = withState('extended', 'setExtended', false)(
    ArtifactsInner as ComponentType<any>,
) as unknown as ComponentType<{ log: UiLog }>;

const deleteCheckerKey = (data: UiFormulaNode, key: string) => {
    let result: Record<string, unknown> | undefined = _.omit(data.$checker, [key]);

    if (_.isEmpty(result)) result = undefined;

    data.change('$checker', result);
};

class InlineChecker extends PureComponent<{ data: UiFormulaNode; [key: string]: unknown }> {
    render() {
        const { data, ...rest } = this.props;

        return (
            <div {...(rest as React.HTMLAttributes<HTMLDivElement>)} className={'InlineChecker-root'}>
                {_.map(data.$checker, (value: unknown, key: string) =>
                    key === 'template' ? null :
                    typeof value === 'string' ?
                        <div key={key} className={'InlineChecker-item'}>
                            <span className='InlineChecker-type CheckerSelectMessage' data-type={key}>
                                {_.get(_.find(CheckerMessage.options, { 0: key }), [1])}
                            </span>

                            <TextField
                                defaultValue={value}
                                className={'InlineChecker-value'}
                                onChange={val => data.change(`$checker[${key}]`, val)}
                            />

                            <div className={'InlineChecker-actionList'}>
                                <div
                                    onClick={() => deleteCheckerKey(data, key)}
                                    className={cx('InlineChecker-action', 'InlineChecker-remove')}
                                >×</div>
                            </div>
                        </div> :
                    <div key={key} className='row-center'>
                        <CheckerLabel type={key}>{key}</CheckerLabel>
                        <InlineCheckerMessage
                            path={data.getChildPath(`$checker[${key}]`)}
                            placeholder={`Message when ${key}`}
                        />
                        <div className={'InlineChecker-actionList'}>
                            <div
                                onClick={() => deleteCheckerKey(data, key)}
                                className={cx('InlineChecker-action', 'InlineChecker-remove')}
                            >×</div>
                        </div>
                    </div>,
                )}
            </div>
        );
    }
}
